import React, { useEffect, useMemo, useState } from 'react';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
  Theme
} from '@material-ui/core';
import AddBoxOutlinedIcon from '@material-ui/icons/AddBoxOutlined';

import { AlertDialog } from 'src/components';
import { LoadingButton } from 'src/components/buttons/LoadingButton';
import { usePermissions, useSnackBar } from 'src/hooks';
import { CustomKeyboardEvent } from 'src/types';
import {
  Contact,
  Supplier,
  SupplierCompanyDetailRequest
} from 'src/types/supplier';
import { ContactField } from './AddContactField';

interface Props {
  isVisible: boolean;
  handleClose: () => void;
  onSavePress: (
    companyName: string,
    companyDetails: SupplierCompanyDetailRequest[],
    contacts: Contact[] | null
  ) => void;
  customButtons?: JSX.Element;
  loading?: boolean;
  errMsg?: string;
  supplier?: Supplier | null;
  onDeletePress?: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: 'relative'
  },
  section: {
    border: '1px solid #d9d9d9',
    borderRadius: 4,
    margin: '10px 0',
    padding: 10,
    position: 'relative'
  }
}));

const emptyCompanyDetail = (
  action?: 'create'
): SupplierCompanyDetailRequest => ({
  name: '',
  address: '',
  terms: '',
  tin: '',
  zip_code: '',
  ...(action && { action })
});

const formatTin = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 13);
  return [
    digits.slice(0, 3),
    digits.slice(3, 6),
    digits.slice(6, 9),
    digits.slice(9, 13)
  ]
    .filter(Boolean)
    .join('-');
};

const SupplierDialogForm = ({
  isVisible = false,
  handleClose,
  onSavePress,
  customButtons,
  errMsg,
  supplier,
  loading = false,
  onDeletePress
}: Props) => {
  const classes = useStyles();
  const snackBar = useSnackBar();
  const { canDeleteSupplier } = usePermissions();
  const isEditSupplier = useMemo(() => !!supplier, [supplier]);
  const showDeleteButton = isEditSupplier && canDeleteSupplier;

  const [companyName, setCompanyName] = useState('');
  const [companyDetails, setCompanyDetails] = useState<
    SupplierCompanyDetailRequest[]
  >([emptyCompanyDetail()]);
  const [contacts, setContacts] = useState<Contact[]>([
    { name: '', contact_no: '', email: '' }
  ]);
  const [errCompanyName, setErrCompanyName] = useState(errMsg || '');
  const [deleteSupplierDialog, setDeleteSupplierDialog] = useState(false);

  const visibleCompanyDetails = companyDetails
    .map((detail, originalIndex) => ({ detail, originalIndex }))
    .filter(({ detail }) => detail.action !== 'delete');

  const visibleContacts = contacts
    .map((contact, originalIndex) => ({ contact, originalIndex }))
    .filter(({ contact }) => contact.action !== 'delete');

  const resetState = () => {
    setCompanyName('');
    setCompanyDetails([emptyCompanyDetail()]);
    setContacts([{ name: '', contact_no: '', email: '' }]);
    setErrCompanyName('');
  };

  const loadSupplier = (value?: Supplier | null) => {
    setCompanyName(value?.company_name || value?.name || '');
    setCompanyDetails(
      value?.company_details?.length
        ? value.company_details.map((detail) => ({
            ...detail,
            action: 'update'
          }))
        : [
            {
              id: null,
              name: value?.company_name || value?.name || '',
              address: value?.address || '',
              terms: value?.terms || '',
              tin: value?.tin || '',
              zip_code: value?.zip_code || '',
              ...(value && { action: 'create' })
            }
          ]
    );
    setContacts(
      value?.contacts?.length
        ? value.contacts.map((contact) => ({
            ...contact,
            name: contact.name || '',
            contact_no: contact.contact_no || '',
            email: contact.email || ''
          }))
        : [
            {
              name: '',
              contact_no: '',
              email: '',
              ...(value && { action: 'create' })
            }
          ]
    );
    setErrCompanyName('');
  };

  useEffect(() => {
    if (isVisible) {
      if (supplier) {
        loadSupplier(supplier);
      } else {
        resetState();
      }
    }
  }, [isVisible, supplier]);

  const updateCompanyDetail = (index: number, field: string, value: string) => {
    setCompanyDetails((current) =>
      current.map((detail, detailIndex) =>
        detailIndex === index
          ? {
              ...detail,
              [field]: value,
              ...(isEditSupplier && {
                action: detail.action === 'create' ? 'create' : 'update'
              })
            }
          : detail
      )
    );
  };

  const addCompanyDetail = () => {
    setCompanyDetails((current) => [
      ...current,
      emptyCompanyDetail(isEditSupplier ? 'create' : undefined)
    ]);
  };

  const removeCompanyDetail = (index: number) => {
    const detail = companyDetails[index];

    if (isEditSupplier && detail?.id) {
      setCompanyDetails((current) =>
        current.map((item, itemIndex) =>
          itemIndex === index ? { ...item, action: 'delete' } : item
        )
      );
      return;
    }

    setCompanyDetails((current) =>
      current.filter((_, itemIndex) => itemIndex !== index)
    );
  };

  const addContact = () => {
    setContacts((current) => [
      ...current,
      {
        name: '',
        contact_no: '',
        email: '',
        ...(isEditSupplier && { action: 'create' })
      }
    ]);
  };

  const removeContact = (index: number) => {
    const contact = contacts[index];

    if (isEditSupplier && contact?.id) {
      setContacts((current) =>
        current.map((item, itemIndex) =>
          itemIndex === index ? { ...item, action: 'delete' } : item
        )
      );
      return;
    }

    setContacts((current) =>
      current.filter((_, itemIndex) => itemIndex !== index)
    );
  };

  const updateContact = (index: number, field: string, value: string) => {
    setContacts((current) =>
      current.map((contact, contactIndex) =>
        contactIndex === index
          ? {
              ...contact,
              [field]: value,
              ...(isEditSupplier && {
                action: contact.action === 'create' ? 'create' : 'update'
              })
            }
          : contact
      )
    );
  };

  const validateCompanyDetails = () => {
    const activeDetails = companyDetails.filter(
      (detail) => detail.action !== 'delete'
    );

    if (activeDetails.length === 0) {
      snackBar.show({
        severity: 'error',
        message: 'Please provide at least one company detail.'
      });
      return false;
    }

    const invalidDetail = activeDetails.find(
      (detail) =>
        !detail.name?.trim() ||
        !detail.address?.trim() ||
        !detail.terms?.trim() ||
        !detail.tin?.trim() ||
        detail.tin.replace(/-/g, '').length < 9 ||
        !detail.zip_code?.trim() ||
        detail.zip_code.length < 4
    );

    if (invalidDetail) {
      snackBar.show({
        severity: 'error',
        message:
          'Each company detail requires a name, address, terms, valid TIN, and 4-digit ZIP code.'
      });
      return false;
    }

    return true;
  };

  const prepareContacts = (): Contact[] | null | undefined => {
    const nonEmptyContacts = contacts.filter(
      (contact) =>
        contact.action === 'delete' ||
        contact.name ||
        contact.contact_no ||
        contact.email
    );

    const invalidContact = nonEmptyContacts.find((contact) => {
      if (contact.action === 'delete') return false;
      const hasName = !!contact.name?.trim();
      const hasContactMethod =
        !!contact.contact_no?.trim() || !!contact.email?.trim();
      const invalidContactNo =
        !!contact.contact_no?.trim() && contact.contact_no.trim().length !== 11;
      const invalidEmail =
        !!contact.email?.trim() &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim());
      return !hasName || !hasContactMethod || invalidContactNo || invalidEmail;
    });

    if (invalidContact) {
      snackBar.show({
        severity: 'error',
        message:
          'Each contact requires a name and a valid 11-digit phone number or email.'
      });
      return undefined;
    }

    if (nonEmptyContacts.length === 0) return null;

    return nonEmptyContacts.map((contact) => ({
      ...(contact.id && { id: contact.id }),
      ...(contact.action && { action: contact.action }),
      ...(contact.action !== 'delete' && {
        name: contact.name?.trim(),
        ...(contact.contact_no?.trim() && {
          contact_no: contact.contact_no.trim()
        }),
        ...(contact.email?.trim() && { email: contact.email.trim() })
      })
    }));
  };

  const onSaveBtnPress = () => {
    if (loading) return;

    if (!companyName.trim()) {
      setErrCompanyName('Please Provide Company Name');
      return;
    }

    if (!validateCompanyDetails()) return;

    const finalContacts = prepareContacts();
    if (finalContacts === undefined) return;

    const finalCompanyDetails = companyDetails.map<
      SupplierCompanyDetailRequest
    >((detail) => {
      if (detail.action === 'delete') {
        return { id: detail.id, action: 'delete' };
      }

      return {
        ...(detail.id && { id: detail.id }),
        ...(detail.action && { action: detail.action }),
        name: detail.name?.trim(),
        address: detail.address?.trim(),
        terms: detail.terms?.trim(),
        tin: detail.tin?.trim(),
        zip_code: detail.zip_code?.trim()
      };
    });

    onSavePress(companyName.trim(), finalCompanyDetails, finalContacts);
  };

  const onCancelBtnPress = () => {
    if (supplier) {
      loadSupplier(supplier);
    } else {
      resetState();
    }
    handleClose();
  };

  const onKeyPress = (event: CustomKeyboardEvent) => {
    if (event.key === 'Enter') onSaveBtnPress();
  };

  return (
    <Dialog
      open={isVisible}
      onClose={onCancelBtnPress}
      aria-labelledby="form-dialog-title"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h4" component="div">
          {isEditSupplier
            ? 'Edit Supplier/Distributor'
            : 'Register Supplier/Distributor'}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ padding: 25 }}>
        <TextField
          error={!!errCompanyName}
          helperText={errCompanyName}
          autoFocus
          margin="dense"
          id="company_name"
          label="Company Name"
          fullWidth
          required
          variant="outlined"
          value={companyName}
          onKeyPress={onKeyPress}
          onChange={(event) => {
            setErrCompanyName('');
            setCompanyName(event.target.value);
          }}
        />

        <Typography
          variant="h6"
          style={{ marginTop: 15, color: 'lightslategray' }}
        >
          Company Detail/s
        </Typography>
        {visibleCompanyDetails.map(
          ({ detail, originalIndex }, displayIndex) => (
            <div
              className={classes.section}
              key={
                detail.id
                  ? `company-detail-${detail.id}`
                  : `new-company-detail-${originalIndex}`
              }
            >
              <Grid container spacing={2}>
                <Grid item xs={11}>
                  <Typography variant="subtitle2">
                    Company Detail {displayIndex + 1}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <IconButton size="small" disabled={visibleCompanyDetails.length === 1}>
                    <DeleteIcon
                      color="secondary"
                      onClick={() => removeCompanyDetail(originalIndex)}
                      style={{ cursor: 'pointer' }}
                    />
                  </IconButton>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Detail Name"
                    fullWidth
                    required
                    variant="outlined"
                    value={detail.name || ''}
                    onChange={(event) =>
                      updateCompanyDetail(
                        originalIndex,
                        'name',
                        event.target.value
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Address"
                    fullWidth
                    required
                    multiline
                    variant="outlined"
                    value={detail.address || ''}
                    onChange={(event) =>
                      updateCompanyDetail(
                        originalIndex,
                        'address',
                        event.target.value
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    margin="dense"
                    label="Terms"
                    fullWidth
                    required
                    variant="outlined"
                    value={(detail.terms || '').replace(/\s*days$/, '')}
                    onChange={(event) => {
                      if (/^\d*$/.test(event.target.value)) {
                        updateCompanyDetail(
                          originalIndex,
                          'terms',
                          event.target.value ? `${event.target.value} days` : ''
                        );
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">days</InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    margin="dense"
                    label="TIN"
                    fullWidth
                    required
                    variant="outlined"
                    value={detail.tin || ''}
                    onChange={(event) =>
                      updateCompanyDetail(
                        originalIndex,
                        'tin',
                        formatTin(event.target.value)
                      )
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    margin="dense"
                    label="ZIP Code"
                    fullWidth
                    required
                    variant="outlined"
                    value={detail.zip_code || ''}
                    inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                    onChange={(event) => {
                      if (/^\d*$/.test(event.target.value)) {
                        updateCompanyDetail(
                          originalIndex,
                          'zip_code',
                          event.target.value
                        );
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </div>
          )
        )}
        <div style={{ textAlign: 'center' }}>
          <Button size="small" color="primary" onClick={addCompanyDetail}>
            <AddBoxOutlinedIcon fontSize="inherit" style={{ marginRight: 5 }} />
            Add Company Detail
          </Button>
        </div>

        <Typography
          variant="h6"
          style={{ marginTop: 15, color: 'lightslategray' }}
        >
          Contact Information/s
        </Typography>
        {visibleContacts.map(({ contact, originalIndex }) => (
          <ContactField
            key={
              contact.id
                ? `contact-${contact.id}`
                : `new-contact-${originalIndex}`
            }
            contact={contact}
            index={originalIndex}
            onRemove={removeContact}
            onChange={updateContact}
          />
        ))}
        <div style={{ textAlign: 'center' }}>
          <Button size="small" color="primary" onClick={addContact}>
            <AddBoxOutlinedIcon fontSize="inherit" style={{ marginRight: 5 }} />
            Add Contact
          </Button>
        </div>
      </DialogContent>
      <DialogActions
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '0 25px 25px'
        }}
      >
        {customButtons || (
          <Grid container spacing={1}>
            {showDeleteButton && (
              <Grid item xs={4}>
                <Button
                  className={classes.wrapper}
                  onClick={() => setDeleteSupplierDialog(true)}
                  color="secondary"
                  variant="contained"
                  fullWidth
                >
                  Delete
                </Button>
              </Grid>
            )}
            <Grid item xs={showDeleteButton ? 4 : 6}>
              <Button
                className={classes.wrapper}
                onClick={onCancelBtnPress}
                variant="outlined"
                fullWidth
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={showDeleteButton ? 4 : 6}>
              <LoadingButton
                title="Save"
                loading={loading}
                onClick={onSaveBtnPress}
                color="primary"
                fullWidth
              />
            </Grid>
          </Grid>
        )}
      </DialogActions>
      <AlertDialog
        title={`Delete ${companyName}`}
        customButtons={
          <>
            <Button onClick={onDeletePress} color="secondary" autoFocus>
              Delete
            </Button>
            <Button
              onClick={() => setDeleteSupplierDialog(false)}
              color="primary"
            >
              Cancel
            </Button>
          </>
        }
        subTitle={`Are you sure you want to delete ${companyName}?`}
        isVisible={deleteSupplierDialog}
      />
    </Dialog>
  );
};

export default SupplierDialogForm;
